import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { API_BASE_URL } from "../../config";
import { APP_TOKEN_KEY } from "./appAuthSlice";

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: { "Content-Type": "application/json" },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem(APP_TOKEN_KEY);
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const apiMessage = (error, fallback) =>
  error.response?.data?.message ||
  error.response?.data?.error ||
  error.message ||
  fallback;

export const updateNote = createAsyncThunk(
  "profile/updateNote",
  async ({ id, notes }, { rejectWithValue }) => {
    try {
      const trimmedNotes = typeof notes === "string" ? notes.trim() : "";
      const res = await api.put(`/api/profile/${id}/note`, { notes: trimmedNotes });
      return res.data;
    } catch (error) {
      return rejectWithValue(apiMessage(error, "Failed to update note"));
    }
  }
);

export const updateFavorite = createAsyncThunk(
  "profile/updateFavorite",
  async ({ id, favoritePlayer, favoriteTeam, favoriteLeague }, { rejectWithValue }) => {
    try {
      const body = {};
      if (favoritePlayer !== undefined) body.favoritePlayer = favoritePlayer;
      if (favoriteTeam !== undefined) body.favoriteTeam = favoriteTeam;
      if (favoriteLeague !== undefined) body.favoriteLeague = favoriteLeague;

      const res = await api.put(`/api/profile/${id}/favorite`, body);
      return res.data;
    } catch (error) {
      return rejectWithValue(apiMessage(error, "Failed to update favorites"));
    }
  }
);

export async function searchFavoriteCatalog(type, params = {}) {
  const res = await api.get(`/api/catalog/${type}`, { params });
  return res.data;
}

export const generateInviteCode = createAsyncThunk(
  "profile/generateInviteCode",
  async (id, { rejectWithValue }) => {
    try {
      const res = await api.post(`/api/profile/${id}/inviteCode`);
      return res.data;
    } catch (error) {
      return rejectWithValue(apiMessage(error, "Failed to generate invite code"));
    }
  }
);

export const checkInviteCodeAvailability = createAsyncThunk(
  "profile/checkInviteCodeAvailability",
  async ({ id, code }, { rejectWithValue }) => {
    try {
      const res = await api.get(`/api/profile/${id}/inviteCode/availability`, {
        params: { code },
      });
      return res.data;
    } catch (error) {
      return rejectWithValue(
        apiMessage(error, "Failed to check invite code availability")
      );
    }
  }
);

export const setCustomInviteCode = createAsyncThunk(
  "profile/setCustomInviteCode",
  async ({ id, inviteCode }, { rejectWithValue }) => {
    try {
      const res = await api.put(`/api/profile/${id}/inviteCode`, { inviteCode });
      return res.data;
    } catch (error) {
      return rejectWithValue(apiMessage(error, "Failed to set invite code"));
    }
  }
);

export const updateProfileInfo = createAsyncThunk(
  "profile/updateProfileInfo",
  async ({ id, name, email, phoneNumber }, { rejectWithValue }) => {
    try {
      const body = {};
      if (name !== undefined) body.name = name;
      if (email !== undefined) body.email = email;
      if (phoneNumber !== undefined) body.phoneNumber = phoneNumber;

      const res = await api.put(`/api/profile/${id}/profileInfo`, body);
      return res.data;
    } catch (error) {
      return rejectWithValue(apiMessage(error, "Failed to update profile information"));
    }
  }
);

export const changePassword = createAsyncThunk(
  "profile/changePassword",
  async ({ id, oldPassword, newPassword }, { rejectWithValue }) => {
    try {
      const res = await api.put(`/api/profile/${id}/password`, {
        oldPassword,
        newPassword,
      });
      return res.data;
    } catch (error) {
      return rejectWithValue(apiMessage(error, "Failed to change password"));
    }
  }
);

export const fetchUserFollowList = createAsyncThunk(
  "profile/fetchUserFollowList",
  async ({ userId, type }, { rejectWithValue }) => {
    try {
      const res = await api.get(`/api/users/${userId}/follow-list`, {
        params: { type },
      });
      return {
        type,
        users: res.data?.users || [],
        count: res.data?.count ?? 0,
      };
    } catch (error) {
      return rejectWithValue(apiMessage(error, "Failed to load connections"));
    }
  }
);

export const toggleFollow = createAsyncThunk(
  "profile/toggleFollow",
  async ({ currentUserId, targetId }, { rejectWithValue }) => {
    try {
      const res = await api.post("/api/users/follow", {
        currentUserId,
        targetId,
      });
      return {
        followed: Boolean(res.data?.followed),
        currentUserId,
        targetId,
      };
    } catch (error) {
      return rejectWithValue(apiMessage(error, "Failed to update follow status"));
    }
  }
);

const profileSlice = createSlice({
  name: "profile",
  initialState: {
    noteStatus: "idle",
    noteError: null,
    favoritesStatus: "idle",
    favoritesError: null,
    inviteCodeStatus: "idle",
    inviteCodeError: null,
    inviteCodeAvailabilityStatus: "idle",
    inviteCodeAvailability: null,
    passwordStatus: "idle",
    passwordError: null,
    profileInfoStatus: "idle",
    profileInfoError: null,
    connectionsStatus: "idle",
    connectionsError: null,
    connectionsType: null,
    connections: [],
  },
  reducers: {
    resetNoteStatus: (state) => {
      state.noteStatus = "idle";
      state.noteError = null;
    },
    resetFavoritesStatus: (state) => {
      state.favoritesStatus = "idle";
      state.favoritesError = null;
    },
    resetInviteCodeStatus: (state) => {
      state.inviteCodeStatus = "idle";
      state.inviteCodeError = null;
      state.inviteCodeAvailabilityStatus = "idle";
      state.inviteCodeAvailability = null;
    },
    resetPasswordStatus: (state) => {
      state.passwordStatus = "idle";
      state.passwordError = null;
    },
    resetProfileInfoStatus: (state) => {
      state.profileInfoStatus = "idle";
      state.profileInfoError = null;
    },
    clearConnections: (state) => {
      state.connectionsStatus = "idle";
      state.connectionsError = null;
      state.connectionsType = null;
      state.connections = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(updateNote.pending, (state) => {
        state.noteStatus = "loading";
        state.noteError = null;
      })
      .addCase(updateNote.fulfilled, (state) => {
        state.noteStatus = "succeeded";
        state.noteError = null;
      })
      .addCase(updateNote.rejected, (state, action) => {
        state.noteStatus = "failed";
        state.noteError = action.payload;
      })
      .addCase(updateFavorite.pending, (state) => {
        state.favoritesStatus = "loading";
        state.favoritesError = null;
      })
      .addCase(updateFavorite.fulfilled, (state) => {
        state.favoritesStatus = "succeeded";
        state.favoritesError = null;
      })
      .addCase(updateFavorite.rejected, (state, action) => {
        state.favoritesStatus = "failed";
        state.favoritesError = action.payload;
      })
      .addCase(generateInviteCode.pending, (state) => {
        state.inviteCodeStatus = "loading";
        state.inviteCodeError = null;
      })
      .addCase(generateInviteCode.fulfilled, (state) => {
        state.inviteCodeStatus = "succeeded";
        state.inviteCodeError = null;
      })
      .addCase(generateInviteCode.rejected, (state, action) => {
        state.inviteCodeStatus = "failed";
        state.inviteCodeError = action.payload;
      })
      .addCase(checkInviteCodeAvailability.pending, (state) => {
        state.inviteCodeAvailabilityStatus = "loading";
        state.inviteCodeAvailability = null;
      })
      .addCase(checkInviteCodeAvailability.fulfilled, (state, action) => {
        state.inviteCodeAvailabilityStatus = "succeeded";
        state.inviteCodeAvailability = action.payload;
      })
      .addCase(checkInviteCodeAvailability.rejected, (state, action) => {
        state.inviteCodeAvailabilityStatus = "failed";
        state.inviteCodeAvailability = action.payload;
      })
      .addCase(setCustomInviteCode.pending, (state) => {
        state.inviteCodeStatus = "loading";
        state.inviteCodeError = null;
      })
      .addCase(setCustomInviteCode.fulfilled, (state) => {
        state.inviteCodeStatus = "succeeded";
        state.inviteCodeError = null;
        state.inviteCodeAvailability = null;
        state.inviteCodeAvailabilityStatus = "idle";
      })
      .addCase(setCustomInviteCode.rejected, (state, action) => {
        state.inviteCodeStatus = "failed";
        state.inviteCodeError = action.payload;
      })
      .addCase(updateProfileInfo.pending, (state) => {
        state.profileInfoStatus = "loading";
        state.profileInfoError = null;
      })
      .addCase(updateProfileInfo.fulfilled, (state) => {
        state.profileInfoStatus = "succeeded";
        state.profileInfoError = null;
      })
      .addCase(updateProfileInfo.rejected, (state, action) => {
        state.profileInfoStatus = "failed";
        state.profileInfoError = action.payload;
      })
      .addCase(changePassword.pending, (state) => {
        state.passwordStatus = "loading";
        state.passwordError = null;
      })
      .addCase(changePassword.fulfilled, (state) => {
        state.passwordStatus = "succeeded";
        state.passwordError = null;
      })
      .addCase(changePassword.rejected, (state, action) => {
        state.passwordStatus = "failed";
        state.passwordError = action.payload;
      })
      .addCase(fetchUserFollowList.pending, (state) => {
        state.connectionsStatus = "loading";
        state.connectionsError = null;
      })
      .addCase(fetchUserFollowList.fulfilled, (state, action) => {
        state.connectionsStatus = "succeeded";
        state.connectionsType = action.payload.type;
        state.connections = action.payload.users;
        state.connectionsError = null;
      })
      .addCase(fetchUserFollowList.rejected, (state, action) => {
        state.connectionsStatus = "failed";
        state.connectionsError = action.payload;
      })
      .addCase(toggleFollow.pending, (state) => {
        state.connectionsError = null;
      })
      .addCase(toggleFollow.fulfilled, (state, action) => {
        const { followed, targetId } = action.payload;
        const listType = state.connectionsType;
        if (listType === "following" && !followed) {
          state.connections = state.connections.filter(
            (row) => String(row._id) !== String(targetId)
          );
        }
      })
      .addCase(toggleFollow.rejected, (state, action) => {
        state.connectionsError = action.payload;
      });
  },
});

export const {
  resetNoteStatus,
  resetFavoritesStatus,
  resetInviteCodeStatus,
  resetPasswordStatus,
  resetProfileInfoStatus,
  clearConnections,
} = profileSlice.actions;
export default profileSlice.reducer;
